import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import {
  subtitlesApi,
  type CandidateRow,
  type MediaKind,
  type ScoreBreakdown,
} from "@/api/subtitles";
import { Button } from "@/components/ui/Button";
import { TextInput } from "@/components/ui/Field";
import { Modal } from "@/components/ui/Modal";
import { Status, type StatusTone } from "@/components/ui/Status";
import { useToast } from "@/components/ui/Toast";
import { cn } from "@/lib/cn";

/**
 * Manual subtitle picker (issue #87).
 *
 * Auto-search picks the top-scoring candidate per (media, language).
 * Sometimes that pick is wrong — wrong release, mis-synced cut, weird
 * forced-only file. This modal exposes the *full* candidate list from
 * every enabled provider, with the per-component score breakdown the
 * scorer used so the user can see why a candidate ranked where it did.
 *
 * Flow:
 *   1. Open the modal — language defaults to the first profile language,
 *      or English if the profile is empty.
 *   2. User picks a language; we POST `/subtitles/candidates` to fan
 *      out across providers in parallel.
 *   3. Rendered as a table sorted by score desc. Per-row "Use this"
 *      button POSTs `/subtitles/download` with the chosen
 *      `(provider, download_url)` to fetch + persist the SRT.
 *   4. On success the modal stays open so the user can pick another
 *      language; an icon next to the language input flips to ✓ to
 *      reflect the success.
 */

interface Props {
  open: boolean;
  onClose: () => void;
  mediaKind: MediaKind;
  mediaId: number;
  /** Eyebrow / title hint, e.g. movie title or "S01E02 — Foo". */
  mediaTitle: string;
  /** Initial language to query. Falls back to "en" when omitted. */
  defaultLanguage?: string;
}

export function SubtitleSearchModal({
  open,
  onClose,
  mediaKind,
  mediaId,
  mediaTitle,
  defaultLanguage,
}: Props) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [language, setLanguage] = useState(defaultLanguage ?? "en");
  // Once the user has downloaded, latch the success state so the
  // language picker flashes ✓ until they search a different language.
  const [downloadedFor, setDownloadedFor] = useState<string | null>(null);

  // Reset transient state every time the modal closes; otherwise
  // a stale ✓ would carry across to the next open.
  useEffect(() => {
    if (!open) {
      setDownloadedFor(null);
    }
  }, [open]);

  const candidatesQ = useQuery({
    queryKey: [
      "subtitles",
      "candidates",
      mediaKind,
      mediaId,
      language.toLowerCase(),
    ],
    queryFn: () =>
      subtitlesApi.candidates({
        media_kind: mediaKind,
        media_id: mediaId,
        language,
      }),
    // Don't auto-fire on open — wait for the user to confirm the
    // language. They'll click Search after picking; that's the gate.
    enabled: open && language.trim().length > 0,
    staleTime: 30_000,
  });

  const downloadMut = useMutation({
    mutationFn: (c: CandidateRow) =>
      subtitlesApi.download({
        media_kind: mediaKind,
        media_id: mediaId,
        language,
        provider: c.provider,
        download_url: c.download_url,
      }),
    onSuccess: () => {
      toast(`Downloaded ${language.toUpperCase()} subtitle`, "success");
      setDownloadedFor(language.toLowerCase());
      // Refresh whatever views were rendering the per-media subtitle
      // list — there's a new row in `subtitles`, the Wanted page may
      // close a gap, etc.
      qc.invalidateQueries({ queryKey: ["subtitles"] });
      qc.invalidateQueries({ queryKey: ["subtitles", "wanted"] });
    },
    onError: (e) =>
      toast(e instanceof Error ? e.message : "Download failed", "error"),
  });

  const candidates = candidatesQ.data?.candidates ?? [];
  const errors = candidatesQ.data?.errors ?? [];
  const isSuccess =
    candidatesQ.isSuccess && downloadedFor === language.toLowerCase();

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Search subtitles"
      eyebrow={mediaTitle}
      width="xl"
    >
      <div className="space-y-4">
        <div className="flex items-end gap-3">
          <label className="block flex-1 space-y-1.5 text-sm">
            <span className="font-medium text-foreground/85">Language</span>
            <TextInput
              value={language}
              onChange={(e) => {
                setLanguage(e.target.value);
                if (downloadedFor) setDownloadedFor(null);
              }}
              maxLength={6}
              spellCheck={false}
              placeholder="en"
              className="font-mono uppercase"
            />
          </label>
          <Button
            onClick={() => candidatesQ.refetch()}
            loading={candidatesQ.isFetching}
            disabled={!language.trim()}
          >
            Search
          </Button>
          {isSuccess && (
            <Status variant="chip" tone="good" className="mb-2.5">
              downloaded
            </Status>
          )}
        </div>

        {errors.length > 0 && (
          <div className="rounded-md border border-warn/40 bg-warn/10 px-3 py-2 text-xs">
            <div className="mb-1 font-medium text-warn">
              {errors.length === 1
                ? "1 provider failed"
                : `${errors.length} providers failed`}
            </div>
            <ul className="space-y-0.5 font-mono text-[11px] text-warn/85">
              {errors.map((err, i) => (
                <li key={i} className="break-all">
                  {err}
                </li>
              ))}
            </ul>
          </div>
        )}

        {candidatesQ.isLoading || candidatesQ.isFetching ? (
          <div className="rounded-md border border-line bg-surface/30 px-4 py-10 text-center text-sm text-foreground/55">
            Searching providers…
          </div>
        ) : candidatesQ.isError ? (
          <div className="rounded-md border border-bad/40 bg-bad/10 px-3 py-3 text-xs text-bad">
            {candidatesQ.error instanceof Error
              ? candidatesQ.error.message
              : "Search failed"}
          </div>
        ) : candidates.length === 0 && candidatesQ.isSuccess ? (
          <div className="rounded-md border border-dashed border-line bg-surface/30 px-4 py-10 text-center text-sm text-foreground/55">
            No candidates returned for{" "}
            <span className="font-mono">{language.toUpperCase()}</span>.
          </div>
        ) : candidates.length > 0 ? (
          <CandidatesTable
            candidates={candidates}
            onDownload={(c) => downloadMut.mutate(c)}
            pendingId={downloadMut.isPending ? downloadMut.variables?.download_url ?? null : null}
          />
        ) : null}
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Table
// ---------------------------------------------------------------------------

function CandidatesTable({
  candidates,
  onDownload,
  pendingId,
}: {
  candidates: CandidateRow[];
  onDownload: (c: CandidateRow) => void;
  pendingId: string | null;
}) {
  return (
    <div className="overflow-hidden rounded-md border border-line bg-surface/30">
      <table className="w-full text-xs">
        <thead className="bg-surface-2/50">
          <tr className="text-left">
            {["Score", "Provider", "Release", "Flags", "Stats", ""].map((h) => (
              <th
                key={h}
                className="px-3 py-2 font-mono text-[10px] font-medium uppercase tracking-[0.12em] text-foreground/55"
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-line/60">
          {candidates.map((c, i) => (
            <CandidateRowView
              key={`${c.provider}:${c.download_url}:${i}`}
              c={c}
              onDownload={() => onDownload(c)}
              pending={pendingId === c.download_url}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CandidateRowView({
  c,
  onDownload,
  pending,
}: {
  c: CandidateRow;
  onDownload: () => void;
  pending: boolean;
}) {
  const tone = scoreTone(c.score);
  return (
    <tr className="transition hover:bg-surface-2/40">
      <td className="px-3 py-2.5 align-top">
        <ScoreCell score={c.score} breakdown={c.score_breakdown} tone={tone} />
      </td>
      <td className="px-3 py-2.5 align-top">
        <Status variant="chip" tone="neutral" className="font-mono">
          {c.provider}
        </Status>
      </td>
      <td className="max-w-md px-3 py-2.5 align-top">
        {c.release_title ? (
          <span className="break-all font-mono text-[11px]">
            {c.release_title}
          </span>
        ) : (
          <span className="font-mono text-[11px] text-foreground/45 italic">
            no release tag
          </span>
        )}
        {c.uploader && (
          <div className="mt-0.5 font-mono text-[10px] text-foreground/45">
            by {c.uploader}
          </div>
        )}
      </td>
      <td className="px-3 py-2.5 align-top">
        <div className="flex flex-wrap gap-1">
          {c.sdh && (
            <Status variant="chip" tone="info">
              SDH
            </Status>
          )}
          {c.forced && (
            <Status variant="chip" tone="warn">
              forced
            </Status>
          )}
          {c.framerate != null && (
            <Status variant="chip" tone="neutral">
              {c.framerate.toFixed(3)} fps
            </Status>
          )}
        </div>
      </td>
      <td className="px-3 py-2.5 align-top font-mono text-[11px] text-foreground/70 tabular-nums">
        {c.download_count != null && (
          <div>{c.download_count.toLocaleString()} dl</div>
        )}
        {c.rating != null && <div>★ {c.rating.toFixed(1)}</div>}
        {c.upload_date && (
          <div className="text-foreground/45">
            {formatUploadDate(c.upload_date)}
          </div>
        )}
      </td>
      <td className="px-3 py-2.5 text-right align-top whitespace-nowrap">
        <Button size="sm" onClick={onDownload} loading={pending}>
          Use this
        </Button>
      </td>
    </tr>
  );
}

// ---------------------------------------------------------------------------
// Score breakdown
// ---------------------------------------------------------------------------

/**
 * Renders the score chip with the per-component breakdown surfacing
 * on hover (native title attribute) AND in an inline summary line so
 * keyboard / screen-reader users see it without hover.
 */
function ScoreCell({
  score,
  breakdown,
  tone,
}: {
  score: number;
  breakdown: ScoreBreakdown;
  tone: StatusTone;
}) {
  const parts = useMemo(() => describeBreakdown(breakdown), [breakdown]);
  const tooltip = parts.length > 0 ? parts.join(" · ") : "no bonuses";
  return (
    <div className="flex flex-col items-start gap-1">
      <span
        className={cn(
          "inline-flex items-center justify-center rounded-md border px-2 py-0.5 font-mono text-[11px] font-medium tabular-nums",
          toneToScoreClass(tone),
        )}
        title={`Score ${score}/100 — ${tooltip}`}
      >
        {score}
      </span>
      {parts.length > 0 && (
        <span className="font-mono text-[10px] text-foreground/55">
          {parts.join(" · ")}
        </span>
      )}
    </div>
  );
}

function describeBreakdown(b: ScoreBreakdown): string[] {
  const out: string[] = [];
  if (b.popularity > 0) out.push(`pop +${b.popularity}`);
  if (b.hash_match > 0) out.push(`hash +${b.hash_match}`);
  if (b.release_match > 0) out.push(`release +${b.release_match}`);
  if (b.framerate_match > 0) out.push(`fps +${b.framerate_match}`);
  if (b.hi_match > 0) out.push(`hi +${b.hi_match}`);
  return out;
}

function scoreTone(score: number): StatusTone {
  if (score >= 90) return "good";
  if (score >= 70) return "accent";
  if (score >= 50) return "info";
  if (score > 0) return "warn";
  return "neutral";
}

function toneToScoreClass(tone: StatusTone): string {
  switch (tone) {
    case "good":
      return "border-good/40 bg-good/10 text-good";
    case "accent":
      return "border-accent/40 bg-accent/10 text-accent";
    case "info":
      return "border-info/40 bg-info/10 text-info";
    case "warn":
      return "border-warn/40 bg-warn/10 text-warn";
    case "bad":
      return "border-bad/40 bg-bad/10 text-bad";
    default:
      return "border-line bg-surface-2 text-foreground/65";
  }
}

function formatUploadDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.valueOf())) return iso;
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}
